import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, MapPin, AlignLeft, Calendar, ExternalLink, CheckCircle, Circle, Video, Phone, Users, Copy, User, Pencil, Trash2, Plus, Save, Loader2, UserMinus, UserPlus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { isGcalDigitalEvent } from '../lib/gcalEventFormat';
import SelectCustomerModal from './manage/SelectCustomerModal';
import type { Customer } from '../lib/customers';

interface Attendee {
  email: string;
  displayName: string | null;
  responseStatus: 'accepted' | 'declined' | 'tentative' | 'needsAction';
  organizer: boolean;
  self: boolean;
}

interface ConferenceEntryPoint {
  entryPointType: string;
  uri?: string;
  label?: string;
  pin?: string;
  regionCode?: string;
}

export interface GoogleCalendarEventData {
  id: string;
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  location: string | null;
  description: string | null;
  google_event_id?: string | null;
  status?: string | null;
  meet_link?: string | null;
  conference_data?: {
    entryPoints?: ConferenceEntryPoint[];
    conferenceSolution?: {
      name: string;
      iconUri?: string;
    } | null;
  } | null;
  attendees?: Attendee[] | null;
  organizer_email?: string | null;
  organizer_name?: string | null;
}

interface GoogleCalendarEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: GoogleCalendarEventData | null;
  onUpdate?: () => void;
}

const responseStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
  accepted: { label: 'Yes', color: 'text-green-700', bg: 'bg-green-50' },
  declined: { label: 'No', color: 'text-red-700', bg: 'bg-red-50' },
  tentative: { label: 'Maybe', color: 'text-yellow-700', bg: 'bg-yellow-50' },
  needsAction: { label: 'Awaiting', color: 'text-gray-500', bg: 'bg-gray-50' },
};

export default function GoogleCalendarEventModal({ isOpen, onClose, event, onUpdate }: GoogleCalendarEventModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [removingAttendee, setRemovingAttendee] = useState<string | null>(null);
  const [newAttendeeEmail, setNewAttendeeEmail] = useState('');
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [isSelectCustomerOpen, setIsSelectCustomerOpen] = useState(false);
  const [isAddingMeet, setIsAddingMeet] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAddConference, setEditAddConference] = useState(false);

  // Local event state so modal reflects changes immediately after save
  const [localEvent, setLocalEvent] = useState<GoogleCalendarEventData | null>(null);

  // Live attendees state (updated after add/remove without needing full refresh)
  const [liveAttendees, setLiveAttendees] = useState<Attendee[] | null>(null);

  // Sync prop event into local state when the modal opens or a new event is passed
  useEffect(() => {
    if (event) {
      setLocalEvent(event);
      setLiveAttendees(null);
    }
  }, [event]);

  // Fetch latest attendee data from Google when modal opens
  useEffect(() => {
    if (!isOpen || !event?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke('gcal-event-edit', {
          body: { action: 'getAttendees', eventId: event.id },
        });
        if (cancelled) return;
        if (data?.deleted) {
          toast('This event was deleted from Google Calendar', { icon: 'ℹ️' });
          onClose();
          onUpdate?.();
          return;
        }
        if (data?.attendees) {
          setLiveAttendees(data.attendees);
        }
      } catch (_) { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, event?.id]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setIsEditing(false);
        setShowDeleteConfirm(false);
        setShowAddAttendee(false);
        setNewAttendeeEmail('');
        setLiveAttendees(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (localEvent && isEditing) {
      setEditTitle(localEvent.title || '');
      const start = localEvent.start_time ? parseISO(localEvent.start_time) : null;
      const end = localEvent.end_time ? parseISO(localEvent.end_time) : null;
      setEditStartDate(start ? format(start, 'yyyy-MM-dd') : '');
      setEditStartTime(start ? format(start, 'HH:mm') : '');
      setEditEndDate(end ? format(end, 'yyyy-MM-dd') : '');
      setEditEndTime(end ? format(end, 'HH:mm') : '');
      setEditLocation(localEvent.location || '');
      setEditDescription(localEvent.description || '');
      setEditAddConference(isGcalDigitalEvent(localEvent.meet_link, localEvent.conference_data));
    }
  }, [localEvent, isEditing]);

  if (!isAnimating && !isOpen) return null;
  if (!localEvent) return null;

  // Use localEvent (which reflects saves) for all display logic
  const ev = localEvent;

  const startTime = ev.start_time ? parseISO(ev.start_time) : null;
  const endTime = ev.end_time ? parseISO(ev.end_time) : null;

  const formatDateDisplay = (date: Date) => format(date, 'EEEE, MMMM d, yyyy');
  const formatTimeDisplay = (date: Date) => format(date, 'h:mm a');

  const getDuration = () => {
    if (!startTime || !endTime) return null;
    const diffMs = endTime.getTime() - startTime.getTime();
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
  };

  const isMultiDay = startTime && endTime && format(startTime, 'yyyy-MM-dd') !== format(endTime, 'yyyy-MM-dd');

  const openInGoogleCalendar = () => {
    if (startTime) {
      const y = startTime.getFullYear();
      const m = startTime.getMonth() + 1;
      const d = startTime.getDate();
      window.open(`https://calendar.google.com/calendar/r/day/${y}/${m}/${d}`, '_blank');
    } else {
      window.open('https://calendar.google.com', '_blank');
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setTimeout(() => setCopiedText(null), 2000);
    });
  };

  const statusLabel = ev.status === 'tentative' ? 'Tentative' : ev.status === 'cancelled' ? 'Cancelled' : 'Confirmed';
  const statusColor = ev.status === 'tentative' ? 'text-yellow-600 bg-yellow-50' : ev.status === 'cancelled' ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50';

  const phoneEntryPoint = ev.conference_data?.entryPoints?.find(
    (ep) => ep.entryPointType === 'phone'
  );
  const morePhoneEntryPoint = ev.conference_data?.entryPoints?.find(
    (ep) => ep.entryPointType === 'more' && ep.uri
  );

  const attendees = liveAttendees || ev.attendees || [];
  const acceptedCount = attendees.filter(a => a.responseStatus === 'accepted').length;
  const awaitingCount = attendees.filter(a => a.responseStatus === 'needsAction' || a.responseStatus === 'tentative').length;
  const declinedCount = attendees.filter(a => a.responseStatus === 'declined').length;

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      const parts = name.trim().split(/\s+/);
      return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name[0].toUpperCase();
    }
    return email[0].toUpperCase();
  };

  const getAvatarColor = (email: string) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-red-400',
    ];
    let hash = 0;
    for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const handleDeletedFromGoogle = () => {
    toast('This event was deleted from Google Calendar', { icon: 'ℹ️' });
    onClose();
    onUpdate?.();
  };

  const extractDeletedFlag = async (error: any): Promise<boolean> => {
    try {
      const ctx = (error as any)?.context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        return !!body?.deleted;
      }
    } catch (_) {}
    return false;
  };

  const handleSave = async () => {
    if (!supabase) return;
    setIsSaving(true);
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const hadMeet = isGcalDigitalEvent(ev.meet_link, ev.conference_data);
      const { data, error } = await supabase.functions.invoke('gcal-event-edit', {
        body: {
          action: 'update',
          eventId: ev.id,
          updates: {
            title: editTitle,
            startDate: editStartDate,
            startTime: editStartTime,
            endDate: editEndDate,
            endTime: editEndTime,
            location: editLocation,
            description: editDescription,
            timeZone,
            addConference: editAddConference && !hadMeet,
            removeConference: !editAddConference && hadMeet,
          },
        },
      });
      if (error) {
        if (await extractDeletedFlag(error)) { handleDeletedFromGoogle(); return; }
        throw error;
      }
      if (data?.deleted) { handleDeletedFromGoogle(); return; }
      if (data?.error) throw new Error(data.error);

      if (data?.event) {
        setLocalEvent(data.event);
        setLiveAttendees(data.event.attendees || null);
      }

      toast.success('Event updated');
      setIsEditing(false);
      onUpdate?.();
    } catch (err: any) {
      console.error('Failed to save event:', err);
      toast.error(err.message || 'Failed to update event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!supabase) return;
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('gcal-event-edit', {
        body: { action: 'delete', eventId: ev.id },
      });
      if (error) {
        if (await extractDeletedFlag(error)) { handleDeletedFromGoogle(); return; }
        throw error;
      }
      if (data?.error && !data?.deleted) throw new Error(data.error);

      toast.success('Event deleted');
      onClose();
      onUpdate?.();
    } catch (err: any) {
      console.error('Failed to delete event:', err);
      toast.error(err.message || 'Failed to delete event');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleAddAttendee = async () => {
    if (!supabase || !newAttendeeEmail.trim()) return;
    const email = newAttendeeEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setAddingAttendee(true);
    try {
      const { data, error } = await supabase.functions.invoke('gcal-event-edit', {
        body: { action: 'addAttendee', eventId: ev.id, attendeeEmail: email },
      });
      if (error) {
        if (await extractDeletedFlag(error)) { handleDeletedFromGoogle(); return; }
        throw error;
      }
      if (data?.deleted) { handleDeletedFromGoogle(); return; }
      if (data?.error) throw new Error(data.error);

      setLiveAttendees(data.attendees);
      setNewAttendeeEmail('');
      setShowAddAttendee(false);
      toast.success('Guest added');
      onUpdate?.();
    } catch (err: any) {
      console.error('Failed to add attendee:', err);
      toast.error(err.message || 'Failed to add guest');
    } finally {
      setAddingAttendee(false);
    }
  };

  const handleSelectCustomer = async (customer: Customer) => {
    setIsSelectCustomerOpen(false);
    const email = customer.email?.trim().toLowerCase();
    if (!email) {
      toast.error('This customer has no email address');
      return;
    }
    if (attendees.some(a => a.email === email)) {
      toast.error('This customer is already a guest');
      return;
    }
    setAddingAttendee(true);
    try {
      const { data, error } = await supabase.functions.invoke('gcal-event-edit', {
        body: { action: 'addAttendee', eventId: ev.id, attendeeEmail: email },
      });
      if (error) {
        if (await extractDeletedFlag(error)) { handleDeletedFromGoogle(); return; }
        throw error;
      }
      if (data?.deleted) { handleDeletedFromGoogle(); return; }
      if (data?.error) throw new Error(data.error);

      setLiveAttendees(data.attendees);
      toast.success(`${customer.first_name} ${customer.last_name} added as guest`);
      onUpdate?.();
    } catch (err: any) {
      console.error('Failed to add customer as attendee:', err);
      toast.error(err.message || 'Failed to add guest');
    } finally {
      setAddingAttendee(false);
    }
  };

  const handleRemoveAttendee = async (email: string) => {
    if (!supabase) return;
    setRemovingAttendee(email);
    try {
      const { data, error } = await supabase.functions.invoke('gcal-event-edit', {
        body: { action: 'removeAttendee', eventId: ev.id, attendeeEmail: email },
      });
      if (error) {
        if (await extractDeletedFlag(error)) { handleDeletedFromGoogle(); return; }
        throw error;
      }
      if (data?.deleted) { handleDeletedFromGoogle(); return; }
      if (data?.error) throw new Error(data.error);

      setLiveAttendees(data.attendees);
      toast.success('Guest removed');
      onUpdate?.();
    } catch (err: any) {
      console.error('Failed to remove attendee:', err);
      toast.error(err.message || 'Failed to remove guest');
    } finally {
      setRemovingAttendee(null);
    }
  };

  const handleAddConference = async () => {
    if (!supabase || !ev) return;
    setIsAddingMeet(true);
    try {
      const { data, error } = await supabase.functions.invoke('gcal-event-edit', {
        body: { action: 'addConference', eventId: ev.id },
      });
      if (error) {
        if (await extractDeletedFlag(error)) { handleDeletedFromGoogle(); return; }
        throw error;
      }
      if (data?.deleted) { handleDeletedFromGoogle(); return; }
      if (data?.error) throw new Error(data.error);

      setLocalEvent(prev => prev ? { ...prev, meet_link: data.meet_link, conference_data: data.conference_data } : prev);
      toast.success('Google Meet added');
      onUpdate?.();
    } catch (err: any) {
      console.error('Failed to add Google Meet:', err);
      toast.error(err.message || 'Failed to add Google Meet');
    } finally {
      setIsAddingMeet(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50 transition-opacity duration-300"
        style={{
          backgroundColor: isVisible ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0)',
          backdropFilter: isVisible ? 'blur(4px)' : 'none',
          WebkitBackdropFilter: isVisible ? 'blur(4px)' : 'none',
        }}
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] transition-transform duration-300 ease-out"
        style={{ transform: isVisible ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className="h-full flex flex-col bg-[#f5f5f7] shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                  {isEditing ? 'Edit Event' : 'Event Details'}
                </h2>
                <p className="text-xs text-gray-500">Google Calendar</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                  title="Edit event"
                >
                  <Pencil className="h-3.5 w-3.5 text-gray-600" />
                </button>
              )}
              <button
                onClick={() => { if (isEditing) { setIsEditing(false); } else { onClose(); } }}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3">

            {/* ============ EDIT MODE ============ */}
            {isEditing ? (
              <>
                {/* Title */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className={inputClass}
                    placeholder="Event title"
                  />
                </div>

                {/* Date & Time */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-purple-600" />
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date & Time</label>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Start</label>
                      <div className="flex gap-2">
                        <input type="date" value={editStartDate} onChange={(e) => setEditStartDate(e.target.value)} className={`${inputClass} flex-1`} />
                        <input type="time" value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)} className={`${inputClass} w-28`} />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">End</label>
                      <div className="flex gap-2">
                        <input type="date" value={editEndDate} onChange={(e) => setEditEndDate(e.target.value)} className={`${inputClass} flex-1`} />
                        <input type="time" value={editEndTime} onChange={(e) => setEditEndTime(e.target.value)} className={`${inputClass} w-28`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Google Meet toggle */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Video className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800">Google Meet</p>
                        <p className="text-xs text-gray-500 truncate">
                          {editAddConference ? 'Video call enabled' : 'In person or location only'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editAddConference}
                      onClick={() => setEditAddConference((v) => !v)}
                      className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-200 ${editAddConference ? 'bg-blue-500' : 'bg-gray-300'}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${editAddConference ? 'translate-x-5' : ''}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</label>
                  </div>
                  <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    className={inputClass}
                    placeholder="Add location"
                  />
                </div>

                {/* Description */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlignLeft className="h-4 w-4 text-orange-600" />
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</label>
                  </div>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className={`${inputClass} min-h-[100px] resize-y`}
                    placeholder="Add description"
                  />
                </div>

                {/* Save / Cancel buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    disabled={isSaving}
                    className="px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              /* ============ VIEW MODE ============ */
              <>
                {/* Title Card */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                    {ev.title || '(No title)'}
                  </h3>
                  <div className="flex items-center gap-2 mt-2.5">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
                      </svg>
                      Google Calendar
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                      {ev.status === 'confirmed' ? <CheckCircle className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                      {statusLabel}
                    </span>
                  </div>
                </div>

                {/* Date & Time Card */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Clock className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Date & Time</p>
                      {ev.all_day ? (
                        <div>
                          {startTime && <p className="text-[15px] font-medium text-gray-900">{formatDateDisplay(startTime)}</p>}
                          {isMultiDay && endTime && <p className="text-sm text-gray-600 mt-0.5">to {formatDateDisplay(endTime)}</p>}
                          <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-100">
                            <span className="text-xs font-medium text-gray-600">All day</span>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {startTime && <p className="text-[15px] font-medium text-gray-900">{formatDateDisplay(startTime)}</p>}
                          {isMultiDay && endTime && <p className="text-sm text-gray-600 mt-0.5">to {formatDateDisplay(endTime)}</p>}
                          <div className="flex items-center gap-2.5 mt-2">
                            {startTime && (
                              <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200/60">
                                <span className="text-sm font-semibold text-gray-800">{formatTimeDisplay(startTime)}</span>
                              </div>
                            )}
                            {startTime && endTime && <span className="text-gray-400 text-lg">&rarr;</span>}
                            {endTime && (
                              <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200/60">
                                <span className="text-sm font-semibold text-gray-800">{formatTimeDisplay(endTime)}</span>
                              </div>
                            )}
                          </div>
                          {getDuration() && (
                            <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-lg bg-purple-50">
                              <span className="text-xs font-medium text-purple-700">{getDuration()}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Google Meet / Conference Card */}
                {!ev.meet_link && (
                  <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Video className="h-4 w-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Video Conference</p>
                        <p className="text-sm text-gray-500 mb-3">No video conferencing added</p>
                        <button
                          onClick={handleAddConference}
                          disabled={isAddingMeet}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1a73e8] text-white text-sm font-medium rounded-lg hover:bg-[#1765cc] active:scale-[0.98] transition-all disabled:opacity-50"
                        >
                          {isAddingMeet ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Video className="h-4 w-4" />
                          )}
                          {isAddingMeet ? 'Adding...' : 'Add Google Meet'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {ev.meet_link && (
                  <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Video className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Video Conference</p>
                        <a
                          href={ev.meet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1a73e8] text-white text-sm font-medium rounded-lg hover:bg-[#1765cc] active:scale-[0.98] transition-all"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                          </svg>
                          Join with Google Meet
                        </a>
                        <div className="mt-2 flex items-center gap-1.5">
                          <p className="text-xs text-gray-500 truncate">{ev.meet_link!.replace('https://', '')}</p>
                          <button
                            onClick={() => copyToClipboard(ev.meet_link!, 'meet')}
                            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            {copiedText === 'meet' ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-gray-400" />}
                          </button>
                        </div>
                        {phoneEntryPoint && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-1.5">
                              <Phone className="h-3.5 w-3.5 text-gray-500" />
                              <p className="text-xs font-medium text-gray-600">Join by phone</p>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm text-gray-900 font-medium">{phoneEntryPoint.label || phoneEntryPoint.uri?.replace('tel:', '')}</p>
                              {phoneEntryPoint.uri && (
                                <button onClick={() => copyToClipboard(phoneEntryPoint.label || phoneEntryPoint.uri!.replace('tel:', ''), 'phone')} className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors">
                                  {copiedText === 'phone' ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-gray-400" />}
                                </button>
                              )}
                            </div>
                            {phoneEntryPoint.pin && <p className="text-xs text-gray-500 mt-0.5">PIN: {phoneEntryPoint.pin}#</p>}
                            {morePhoneEntryPoint?.uri && (
                              <a href={morePhoneEntryPoint.uri} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium">
                                <Phone className="h-3 w-3" /> More phone numbers
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Attendees Card */}
                <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Users className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      {attendees.length > 0 && (
                        <p className="text-sm text-gray-700 mb-3">
                          {attendees.length} guest{attendees.length !== 1 ? 's' : ''}
                          {acceptedCount > 0 && <span className="text-gray-400"> &middot; {acceptedCount} yes</span>}
                          {awaitingCount > 0 && <span className="text-gray-400"> &middot; {awaitingCount} awaiting</span>}
                          {declinedCount > 0 && <span className="text-gray-400"> &middot; {declinedCount} no</span>}
                        </p>
                      )}
                      <div className="space-y-2">
                        {attendees.map((attendee, idx) => {
                          const rsvp = responseStatusConfig[attendee.responseStatus] || responseStatusConfig.needsAction;
                          return (
                            <div key={idx} className="flex items-center gap-2.5 group">
                              <div className={`w-7 h-7 rounded-full ${getAvatarColor(attendee.email)} flex items-center justify-center flex-shrink-0`}>
                                <span className="text-[10px] font-bold text-white">{getInitials(attendee.displayName, attendee.email)}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-sm text-gray-900 font-medium truncate">{attendee.displayName || attendee.email}</p>
                                  {attendee.organizer && (
                                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Organizer</span>
                                  )}
                                </div>
                                {attendee.displayName && <p className="text-xs text-gray-500 truncate">{attendee.email}</p>}
                              </div>
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${rsvp.bg} ${rsvp.color}`}>{rsvp.label}</span>
                              {!attendee.organizer && (
                                <button
                                  onClick={() => handleRemoveAttendee(attendee.email)}
                                  disabled={removingAttendee === attendee.email}
                                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-all flex-shrink-0"
                                  title="Remove guest"
                                >
                                  {removingAttendee === attendee.email
                                    ? <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                                    : <UserMinus className="h-3.5 w-3.5 text-red-400" />
                                  }
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Add attendee */}
                      {showAddAttendee ? (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex gap-2">
                            <input
                              type="email"
                              value={newAttendeeEmail}
                              onChange={(e) => setNewAttendeeEmail(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleAddAttendee(); }}
                              placeholder="Enter email address"
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              autoFocus
                            />
                            <button
                              onClick={handleAddAttendee}
                              disabled={addingAttendee || !newAttendeeEmail.trim()}
                              className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0"
                            >
                              {addingAttendee ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                            </button>
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <button onClick={() => { setShowAddAttendee(false); setNewAttendeeEmail(''); }} className="text-xs text-gray-500 hover:text-gray-700">
                              Cancel
                            </button>
                            <span className="text-xs text-gray-300">or</span>
                            <button
                              onClick={() => setIsSelectCustomerOpen(true)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                            >
                              <UserPlus className="h-3 w-3" /> Select Customer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center gap-3">
                          <button
                            onClick={() => setShowAddAttendee(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
                          >
                            <Plus className="h-3.5 w-3.5" /> Add guest
                          </button>
                          <span className="text-xs text-gray-300">or</span>
                          <button
                            onClick={() => setIsSelectCustomerOpen(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
                          >
                            <UserPlus className="h-3.5 w-3.5" /> Select Customer
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location Card */}
                {ev.location && (
                  <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Location</p>
                        <p className="text-sm text-gray-900 whitespace-pre-line">{ev.location}</p>
                        <button
                          onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location!)}`, '_blank')}
                          className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <MapPin className="h-3 w-3" /> Open in Maps
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Organizer Card */}
                {attendees.length === 0 && ev.organizer_email && (
                  <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Organizer</p>
                        <p className="text-sm font-medium text-gray-900">{ev.organizer_name || ev.organizer_email}</p>
                        {ev.organizer_name && <p className="text-xs text-gray-500">{ev.organizer_email}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Description Card */}
                {ev.description && (
                  <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <AlignLeft className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Description</p>
                        <div
                          className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline"
                          dangerouslySetInnerHTML={{ __html: ev.description }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="space-y-2">
                  <button
                    onClick={openInGoogleCalendar}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-xl border border-gray-200/60 shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition-all"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in Google Calendar
                  </button>

                  {/* Delete Event */}
                  {showDeleteConfirm ? (
                    <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                      <p className="text-sm font-medium text-red-800 mb-3">Are you sure you want to delete this event?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={isDeleting}
                          className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-xl border border-red-200 shadow-sm text-sm font-medium text-red-600 hover:bg-red-50 active:scale-[0.98] transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Event
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-5 py-3 border-t border-gray-200/60 bg-white" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            <p className="text-xs text-gray-400 text-center">
              Synced from Google Calendar
            </p>
          </div>
        </div>
      </div>
      <SelectCustomerModal
        isOpen={isSelectCustomerOpen}
        onClose={() => setIsSelectCustomerOpen(false)}
        onSelect={handleSelectCustomer}
        baseZIndex={100300}
      />
    </>,
    document.body
  );
}
